import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { LONG_NARDY_BOARD_STATE, STARTING_BOARD_STATE } from "../Constants";
import { GameBoardState, GameMode, GameResult, HitStatus, Move, Player } from "../Types";

export const gameBoardSlice = createSlice({
  name: "gameBoardState",
  initialState: STARTING_BOARD_STATE,
  reducers: {
    setGameBoardState: (
      _,
      action: { type: string; payload: GameBoardState }
    ) => {
      return action.payload;
    },
    reset: () => STARTING_BOARD_STATE,
    resetForMode: (_, action: PayloadAction<GameMode>) => {
      return action.payload === GameMode.LongNardy
        ? LONG_NARDY_BOARD_STATE
        : STARTING_BOARD_STATE;
    },
  },
});

export function deepCloneGameBoardState(
  gameBoardState: GameBoardState
): GameBoardState {
  const pointsState = [];
  for (let i = 0; i <= 23; i++) {
    const newPointState = {
      ...gameBoardState.pointsState[i],
    };
    pointsState.push(newPointState);
  }

  return {
    pointsState: pointsState,
    barState: {
      ...gameBoardState.barState,
    },
    homeState: {
      ...gameBoardState.homeState,
    },
  };
}

export function applyMoveToGameBoardState(
  gameBoardState: GameBoardState,
  move: Move
): GameBoardState {
  let movingPlayer = move.checkerOwner;
  let nonMovingPlayer =
    move.checkerOwner === Player.One ? Player.Two : Player.One;

  const result = deepCloneGameBoardState(gameBoardState);

  if (move.from === "BAR") {
    result.barState[movingPlayer] -= 1;
  } else if (move.from === "HOME") {
    result.homeState[movingPlayer] -= 1;
  } else {
    const fromPoint = result.pointsState[move.from];
    fromPoint[movingPlayer] -= 1;
    if (move.hitStatus === HitStatus.UndoesHit) {
      if (fromPoint[movingPlayer] !== 0 || fromPoint[nonMovingPlayer] !== 0) {
        console.error(
          "Trying to undo a hit but from point is unexpectedly occupied."
        );
        console.error(gameBoardState);
        console.error(move);
      }
      if (result.barState[nonMovingPlayer] <= 0) {
        console.error(
          "Trying to undo a hit but non-moving player has no checkers on the bar."
        );
        console.error(gameBoardState);
        console.error(move);
      }
      result.barState[nonMovingPlayer] -= 1;
      fromPoint[nonMovingPlayer] += 1;
    }
  }

  if (move.to === "HOME") {
    result.homeState[movingPlayer] += 1;
  } else if (move.to === "BAR") {
    result.barState[movingPlayer] += 1;
  } else {
    const toPoint = result.pointsState[move.to];
    if (toPoint[nonMovingPlayer] > 1) {
      console.error(
        "Trying to apply invalid move. Destination occupied by 2+ opposing checkers"
      );
      console.error(gameBoardState);
      console.error(move);
    }
    if (move.hitStatus === HitStatus.IsHit) {
      if (toPoint[nonMovingPlayer] !== 1) {
        console.error(
          "Trying to apply a hit but destination does not have a single opposing checker."
        );
        console.error(gameBoardState);
        console.error(move);
      }
      result.barState[nonMovingPlayer] += 1;
      toPoint[nonMovingPlayer] -= 1;
    } else {
      if (toPoint[nonMovingPlayer] > 0) {
        console.error(
          "Trying to apply a non-hit move but destination contains opposing checkers."
        );
        console.error(gameBoardState);
        console.error(move);
      }
    }
    toPoint[movingPlayer] += 1;
  }

  return result;
}

export function didPlayerWin(
  gameBoardState: GameBoardState,
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon
): GameResult {
  // Check for any checkers on bar or points.
  if (
    gameBoardState.barState[currentPlayer] > 0 ||
    gameBoardState.pointsState.some((point) => point[currentPlayer] > 0)
  ) {
    return GameResult.NotOver;
  }

  // В длинных нардах нет гаммона/бэкгаммона — только обычная победа
  if (gameMode === GameMode.LongNardy) {
    return GameResult.PlayerWon;
  }

  const otherPlayer = currentPlayer === Player.One ? Player.Two : Player.One;
  if (gameBoardState.homeState[otherPlayer] > 0) {
    return GameResult.PlayerWon;
  }

  let otherPlayerHasCheckerInWinnersHomeBoard = false;
  if (otherPlayer === Player.One) {
    for (let i = 18; i <= 23; i++) {
      if (gameBoardState.pointsState[i][otherPlayer] > 0) {
        otherPlayerHasCheckerInWinnersHomeBoard = true;
        break;
      }
    }
  } else {
    for (let i = 0; i <= 5; i++) {
      if (gameBoardState.pointsState[i][otherPlayer] > 0) {
        otherPlayerHasCheckerInWinnersHomeBoard = true;
        break;
      }
    }
  }

  if (
    gameBoardState.barState[otherPlayer] > 0 ||
    otherPlayerHasCheckerInWinnersHomeBoard
  ) {
    return GameResult.PlayerWonBackgammon;
  }

  return GameResult.PlayerWonGammon;
}

export function pipCount(gameBoardState: GameBoardState, player: Player) {
  let result = 0;
  result += gameBoardState.barState[player] * 25;
  for (let i = 0; i < 24; i++) {
    let numCheckers = gameBoardState.pointsState[i][player];
    let multiplier = player === Player.One ? i + 1 : 24 - i;
    result += numCheckers * multiplier;
  }
  return result;
}

export function areBoardStatesEquivalent(
  boardA: GameBoardState,
  boardB: GameBoardState
): boolean {
  if (
    boardA.barState[Player.One] !== boardB.barState[Player.One] ||
    boardA.barState[Player.Two] !== boardB.barState[Player.Two]
  ) {
    return false;
  }

  if (
    boardA.homeState[Player.One] !== boardB.homeState[Player.One] ||
    boardA.homeState[Player.Two] !== boardB.homeState[Player.Two]
  ) {
    return false;
  }

  for (let i = 0; i <= 23; i++) {
    if (
      boardA.pointsState[i][Player.One] !== boardB.pointsState[i][Player.One] ||
      boardA.pointsState[i][Player.Two] !== boardB.pointsState[i][Player.Two]
    ) {
      return false;
    }
  }

  return true;
}

// Action creators are generated for each case reducer function
export const { setGameBoardState, reset, resetForMode } = gameBoardSlice.actions;

export default gameBoardSlice.reducer;
